import React, { useState } from "react";
import * as XLSX from "xlsx";
import { db } from "../../firebase"; 
import { collection, addDoc } from "firebase/firestore";
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const UploadMaterialComposites = () => { // ✅ Updated component name
  const [fileName, setFileName] = useState("");
  const [uploadedData, setUploadedData] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to convert Excel date values to JavaScript dates
  const parseExcelDate = (excelDate) => {
    const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
    const parsedDate = new Date(excelEpoch.getTime() + (excelDate - 1) * 86400000); // Adjust for Excel's base date
    return parsedDate.toISOString().split("T")[0]; // Return date in YYYY-MM-DD format
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames[0]; 
        const worksheet = workbook.Sheets[sheetName];

        // Convert worksheet data to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Process each row to convert Excel dates
        const processedData = jsonData.map((row) => {
          const newRow = { ...row };

          // Identify potential date fields and parse them
          for (const key in newRow) {
            if (!isNaN(newRow[key]) && typeof newRow[key] === "number" && newRow[key] > 40000) {
              newRow[key] = parseExcelDate(newRow[key]);
            }
          }

          return newRow;
        });

        setUploadedData(processedData);
      } catch (err) {
        setError("Error processing file. Please ensure it's a valid Excel file.");
        console.error("Error processing file:", err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUploadToFirebase = async () => {
    if (uploadedData.length === 0) {
      setError("No data to upload.");
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadStatus("Uploading...");

    try {
      const compositesCollection = collection(db, "materialComposites"); // ✅ Updated collection name
      for (const entry of uploadedData) {
        await addDoc(compositesCollection, entry);
      }
      setUploadStatus("Upload successful!");
    } catch (error) {
      console.error("Error uploading data: ", error);
      setError("Failed to upload data. Please try again.");
      setUploadStatus("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearData = () => {
    setUploadedData([]);
    setFileName("");
    setUploadStatus("");
    setError(null);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CloudUploadIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Upload Material Composites
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" gutterBottom>
                Upload Excel File
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                  />
                </Button>
                {fileName && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: {fileName}
                  </Typography>
                )}
                {fileName && (
                  <Tooltip title="Clear">
                    <IconButton onClick={handleClearData} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {uploadStatus && (
                <Alert 
                  severity={uploadStatus.includes("successful") ? "success" : "info"}
                  onClose={() => setUploadStatus("")}
                >
                  {uploadStatus}
                </Alert>
              )}

              {isUploading && (
                <Box sx={{ width: '100%' }}>
                  <LinearProgress />
                </Box>
              )}

              <Button
                variant="contained"
                color="primary"
                onClick={handleUploadToFirebase}
                disabled={uploadedData.length === 0 || isUploading}
                startIcon={uploadStatus.includes("successful") ? <CheckCircleIcon /> : <CloudUploadIcon />}
                sx={{ textTransform: 'none', mt: 2 }}
              >
                {isUploading ? "Uploading..." : "Upload to Firebase"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {uploadedData.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Preview Data
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {Object.keys(uploadedData[0]).map((header) => (
                        <TableCell key={header}>{header}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadedData.slice(0, 5).map((row, index) => (
                      <TableRow key={index}>
                        {Object.values(row).map((value, cellIndex) => (
                          <TableCell key={cellIndex}>{value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {uploadedData.length > 5 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Showing 5 of {uploadedData.length} rows
                </Typography>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UploadMaterialComposites; // ✅ Updated export name
